export const handleScheduleTime = (schedule) => {
  const { time, timezone } = schedule;

  const [hours, minutes] = String(time).split(':');
  const hoursIn12HourFormat = hours % 12 || 12;
  const period = hours >= 12 ? 'pm' : 'am';
  const timeString = `${hoursIn12HourFormat}:${minutes} ${period}`;

  const timezoneAbbr = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    timeZoneName: 'short',
  })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;

  return timezoneAbbr ? `${timeString} ${timezoneAbbr}` : timeString;
};

// endDate is a ms epoch string already set to end-of-day, so a brief ending today is still active.
export const isScheduleExpired = (schedule) => {
  const endDate = Number(schedule?.endDate);
  return Number.isFinite(endDate) && endDate > 0 && endDate < Date.now();
};

export const NOT_SCHEDULED = 'Not scheduled';

const formatLastRun = (schedule) => {
  const endDate = Number(schedule?.endDate);
  if (!Number.isFinite(endDate) || endDate <= 0) {
    return '';
  }
  // endDate marks end-of-day UTC; formatting locally would roll a positive offset to the next day.
  return new Date(endDate).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const handleFullSchedule = (schedule) => {
  const formattedTime = handleScheduleTime(schedule);
  const { interval, dayOfWeek, weekNumber } = schedule;

  const weekNumberMap = {
    1: 'first',
    '-1': 'last',
  };

  const dayOfWeekMap = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
  };

  let startOrEnd = weekNumberMap[weekNumber] || '';
  let day = dayOfWeekMap[dayOfWeek] || '';

  // an expired schedule reports when it last ran; only a live one advertises its cadence.
  if (isScheduleExpired(schedule)) {
    const lastRun = formatLastRun(schedule);
    return lastRun ? `Expired\nLast ran ${lastRun}` : 'Expired';
  }

  switch (interval) {
    case 1:
      return `Active\nDaily, at ${formattedTime}`;
    case 2:
      return `Active\nWeekly, ${day} at ${formattedTime}`;
    case 3:
      return `Active\nMonthly, ${startOrEnd} ${day} at ${formattedTime}`;
    case 4:
      return `Active\nYearly, ${startOrEnd} ${day} at ${formattedTime}`;
    default:
      return 'Active';
  }
};

export const handleSubscription = (subscriptions) => {
  // a list with no subscription was never scheduled; name that rather than leaving the column blank.
  if (!subscriptions?.length) {
    return NOT_SCHEDULED;
  }

  let latestSubscription = subscriptions[subscriptions.length - 1];
  // prefer a live subscription over a switched-off one that happens to sort later.
  for (let i = subscriptions.length - 1; i >= 0; i--) {
    if (subscriptions[i]?.active === 1) {
      latestSubscription = subscriptions[i];
      break;
    }
  }
  if (!latestSubscription) {
    return NOT_SCHEDULED;
  }
  if (latestSubscription.active !== 1) {
    return 'Inactive';
  }

  const { schedules } = latestSubscription;
  if (!schedules?.length) {
    return 'Inactive';
  }

  let latestSchedule = '';
  //find the lastest active schedule for the subscription
  for (let i = schedules.length - 1; i >= 0; i--) {
    if (schedules[i]?.active === 1) {
      latestSchedule = schedules[i];
      break;
    }
  }

  // an expired subscription has no active schedule left, so report the last one's end
  // date; a schedule that is merely switched off has nothing to advertise.
  if (!latestSchedule) {
    const lastSchedule = schedules[schedules.length - 1];
    return isScheduleExpired(lastSchedule) ? handleFullSchedule(lastSchedule) : 'Inactive';
  }
  if (!latestSchedule.time) {
    return 'Inactive';
  }

  return handleFullSchedule(latestSchedule);
};
